import { Button, type ContextMenuItem, DataTable, Select } from "@design";
import { ArrowRightLeft, Cpu, Plus, Settings2, Star, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { AiModelResponse } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { RowMenu } from "@/components/build/RowMenu";
import { Text } from "@/components/Text";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { useModelColumns } from "./modelColumns";
import { assignableTiers, SORT_OPTIONS, type SortOption, sortModels, usageCount } from "./modelOptions";
import { useModelActions } from "./useModelActions";
import { useAdminModels, useTierDefaults } from "./useModelData";

/** /admin/models: the model catalog. Defaults per tier live on /admin/model-defaults. */
export function ModelsPage() {
  const { t } = useTranslation("adminModels");
  const navigate = useNavigate();
  const canEdit = useAdminCanEdit();
  const [sortBy, setSortBy] = useState<SortOption>("name");
  const list = useAdminModels();
  const defaults = useTierDefaults().data?.data.defaults ?? [];
  const models = list.data?.data ?? [];
  const rows = useMemo(() => sortModels(list.data?.data ?? [], sortBy), [list.data, sortBy]);
  const actions = useModelActions(models);
  const columns = useModelColumns({
    models,
    defaults,
    canEdit,
    onToggle: (model, enabled) => void actions.setEnabled(model, enabled),
  });

  const rowMenu = (model: AiModelResponse) => {
    const items: ContextMenuItem[] = [
      {
        label: t("menu.configure"),
        icon: <Settings2 size={16} />,
        onSelect: () => navigate(`/admin/models/${encodeURIComponent(model.id)}`),
      },
      ...assignableTiers(model, models, defaults).map((tier) => ({
        label: t("menu.setDefault", { tier: tier.label }),
        icon: <Star size={16} />,
        onSelect: () => actions.setDefault(model, tier),
      })),
    ];
    if (usageCount(model) > 0) {
      items.push({ label: t("menu.move"), icon: <ArrowRightLeft size={16} />, onSelect: () => actions.move(model) });
    }
    items.push({
      label: t("menu.delete"),
      icon: <Trash2 size={16} />,
      danger: true,
      onSelect: () => void actions.deleteModel(model),
    });
    return <RowMenu name={model.name} items={items} />;
  };

  return (
    <AdminPage
      title={t("title")}
      subtitle={t("subtitle")}
      actions={
        <Button variant="primary" leadingIcon={<Plus size={16} />} onClick={() => navigate("/admin/models/new")}>
          {t("add")}
        </Button>
      }
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Text variant="body-sm" tone="tertiary">
          {t("orderNote")}
        </Text>
        <div className="md:w-56">
          <Select
            size="sm"
            aria-label={t("sort.label")}
            value={sortBy}
            options={SORT_OPTIONS.map((value) => ({ value, label: t(`sort.${value}`) }))}
            onChange={(value) => setSortBy(value as SortOption)}
          />
        </div>
      </div>
      {list.isError ? (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("tableLabel")}
          columns={columns}
          rows={rows}
          rowKey={(model) => model.id}
          loading={list.isPending}
          empty={<EmptyState icon={<Cpu size={32} />} title={t("empty")} />}
          onRowClick={(model) => navigate(`/admin/models/${encodeURIComponent(model.id)}`)}
          renderActions={canEdit ? rowMenu : undefined}
          actionsLabel={t("columns.actions")}
        />
      )}
      {actions.dialog}
    </AdminPage>
  );
}
